import { Link } from "react-router-dom";
import { ClipboardList } from "lucide-react";

import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { itemsThatFit, remPx, useMeasuredHeight } from "@/hooks/useFittingCount";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

import { SEVERITY_DOT } from "../findingPresentation";
import { useBriefCardState, type BriefCardState } from "../hooks/useBriefCardState";
import type { BriefFinding } from "../types";

/**
 * A suggestion row is `h-10` plus the hairline between rows — the height of a
 * My tasks row beside it, so the two lists line up row for row. The footer is
 * `pt-2.5` plus an `text-xs` line, as under My tasks. In rem: the app scales
 * its root font, so pixels come from `remPx()`.
 */
const SUGGESTION_ROW_REM = 2.5;
const FOOTER_REM = 0.625 + 1;

interface BriefDashboardCardProps {
  buildingId: number;
  projectId: number;
}

/**
 * Project-dashboard Brief section, beside My tasks. Everything in it opens the
 * Brief page.
 *
 * Not a card, like My tasks: when Franklin has open suggestions, the Franklin
 * mark sits beside the section title and the suggestions are a plain list,
 * worst first. Otherwise it is just the brief icon in the middle — "No brief
 * yet" with "Start the brief", or "Go to brief".
 *
 * It never decides the row's height. Its content is placed absolutely inside
 * the row, so My tasks sets the height and the suggestions fill it — as many
 * as fit, then "+N more". Rows are as tall as My tasks rows and both lists
 * close with the same footer line, so with enough suggestions the two lists
 * end on the same line. The minimum height is one row's worth (more for the
 * icon prompt), so Brief never makes the row taller than My tasks needs.
 *
 * Renders nothing for a viewer without access to briefs.
 */
export function BriefDashboardCard({ buildingId, projectId }: BriefDashboardCardProps) {
  const { t } = useLanguage();
  const state = useBriefCardState(projectId);

  if (state.kind === "hidden") return null;

  const href = `/buildings/${buildingId}/brief?project=${projectId}`;
  const suggestions = state.kind === "suggestions" ? state.suggestions : null;

  return (
    <section
      className={cn(
        "flex min-w-0 flex-col",
        // Label row (h-7) + gap (mt-3) + one row (h-10 + borders) + footer
        // (pt-2.5 + a text-xs line): exactly one My tasks row's section.
        state.kind === "suggestions" ? "min-h-[calc(6.625rem+2px)]" : "min-h-[9.25rem]"
      )}
      data-testid="brief-dashboard-card"
    >
      {/* min-h-7 matches My tasks' label row (its "Due within" chip), so both
          lists start on the same line. */}
      <div className="flex min-h-7 items-center gap-2">
        <SettingsSectionLabel>{t.brief.sidebar}</SettingsSectionLabel>
        {suggestions && (
          <>
            <FranklinLogo size="xs" showBorder={false} alt="Franklin" />
            <span
              className="flex-shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground"
              aria-label={`${t.brief.findings.suggestions}: ${suggestions.length}`}
            >
              {suggestions.length}
            </span>
          </>
        )}
      </div>
      <div className="relative mt-3 flex-1">
        <div className="absolute inset-0 flex flex-col">
          {state.kind === "suggestions" ? (
            <SuggestionsList href={href} suggestions={state.suggestions} />
          ) : (
            <BriefPrompt href={href} state={state} />
          )}
        </div>
      </div>
    </section>
  );
}

function SuggestionsList({ href, suggestions }: { href: string; suggestions: BriefFinding[] }) {
  const { t } = useLanguage();
  // The whole area the row gives the list, footer included; the footer
  // follows the last row (like "View all my tasks"), and the rows fill the
  // rest. The list's top border is the one pixel the rows' hairlines miss.
  const [areaRef, areaHeight] = useMeasuredHeight<HTMLDivElement>();
  const rem = remPx();
  const shownCount = itemsThatFit(
    areaHeight == null ? null : areaHeight - FOOTER_REM * rem - 1,
    suggestions.length,
    { rowPx: SUGGESTION_ROW_REM * rem + 1, morePx: 0, beforeMeasure: 3 }
  );
  const shown = suggestions.slice(0, shownCount);
  const more = suggestions.length - shown.length;

  return (
    <div ref={areaRef} className="min-h-0 flex-1 overflow-hidden">
      <ul
        className="divide-y divide-neutral-200 border-y border-neutral-200 px-2"
        aria-label={t.brief.findings.suggestions}
      >
        {shown.map((finding) => (
          <li key={finding.id}>
            <Link
              to={href}
              className="-mx-2 flex h-10 w-[calc(100%+1rem)] min-w-0 items-center gap-2 rounded-md px-2 text-sm transition-colors hover:bg-[#efeff1]"
            >
              <span
                className={cn("h-2 w-2 flex-shrink-0 rounded-full", SEVERITY_DOT[finding.severity])}
                aria-label={t.brief.findings.severity[finding.severity]}
                role="img"
              />
              <span className="min-w-0 truncate text-neutral-800" title={finding.title}>
                {finding.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3 pt-2.5">
        <Link to={href} className="text-xs font-medium text-primary hover:underline">
          {t.brief.dashboardCard.goToBrief} →
        </Link>
        {more > 0 && (
          <span className="text-xs text-muted-foreground">
            {fill(t.brief.dashboardCard.moreSuggestions, { count: String(more) })}
          </span>
        )}
      </div>
    </div>
  );
}

function BriefPrompt({
  href,
  state,
}: {
  href: string;
  state: Exclude<BriefCardState, { kind: "hidden" | "suggestions" }>;
}) {
  const { t } = useLanguage();
  const loading = state.kind === "loading";
  const notStarted = state.kind === "notStarted";
  const action =
    notStarted && state.canStart ? t.brief.empty.start : t.brief.dashboardCard.goToBrief;

  return (
    <Link
      to={href}
      aria-busy={loading || undefined}
      aria-label={loading ? t.brief.title : undefined}
      className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[12px] text-center transition-colors hover:bg-[#fafafa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="brief-prompt"
    >
      <ClipboardList className="h-10 w-10 text-primary/50" aria-hidden />
      {notStarted && <p className="text-sm text-neutral-700">{t.brief.empty.title}</p>}
      {/* While loading the action is unknown — say nothing that a later frame
          could contradict ("Start the brief" over an existing brief). */}
      {!loading && (
        <span className="mt-1 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
          {action}
        </span>
      )}
    </Link>
  );
}
